import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Transition } from "react-transition-group";
import { Animation } from "../../../../../components/Animation";
import Input from "../../../../../components/Form/Input";
import Button from "../../../../../components/Button";
import Icon from "../../../../../components/Icon";
import linkIcon from "../../../../../assets/icons/chain-bold.svg";
import {
  ModalBackdrop,
  Modal,
  ModalDialog,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Title,
  Close,
  ModalAnimation,
  ErrorText,
} from "./styles";
import closeIcon from "../../../../../assets/icons/close_circled.svg";
import { isValidInstagramReelUrl, extractInstagramReelId } from "../../../../../helpers/instagram-reels";

interface Props {
  onInsertUrl: (url: string) => void;
  onClose: () => void;
}

const InsertInstagramReelsModal: React.FC<Props> = ({ onInsertUrl, onClose }) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(false);

  const modalDOM = document.getElementById("modal") as HTMLElement;

  // Esc para cerrar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Gestión de estilos del body/HTML cuando el modal está abierto
  useEffect(() => {
    // Aplicar estilos cuando el modal se monta
    document.body.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    document.documentElement.style.overflow = "hidden";

    // Limpiar estilos cuando el modal se desmonta
    return () => {
      document.body.style.height = "auto";
      document.body.style.overflow = "auto";
      document.documentElement.style.height = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (url.trim()) {
      const valid = isValidInstagramReelUrl(url);
      setIsValid(valid);
      setError(valid ? "" : "URL de Instagram Reel inválida");
    } else {
      setIsValid(false);
      setError("");
    }
  }, [url]);

  const handleSubmit = () => {
    if (isValid && url.trim()) {
      onInsertUrl(url.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) {
      handleSubmit();
    }
  };

  const handleBackdropClick = () => {
    onClose();
  };

  if (!modalDOM) {
    return null;
  }

  return ReactDOM.createPortal(
    <Transition in={true} timeout={350} mountOnEnter>
      {(state) => (
        <>
          <ModalAnimation
            name="fadeIn"
            className={`animation-${state}`}
          >
            <Modal>
              <ModalDialog width={420}>
                <ModalContent>
                  <ModalHeader>
                    <Close onClick={onClose}>
                      <Icon
                        icon={closeIcon}
                        size="24px"
                        color="black"
                      />
                    </Close>
                    <Title>Insertar Instagram Reel</Title>
                  </ModalHeader>

                  <ModalBody onKeyPress={handleKeyPress}>
                    <div style={{ marginBottom: "16px" }}>
                      <p style={{ marginBottom: "12px", color: "#666", fontSize: "14px" }}>
                        Pega la URL del Instagram Reel que quieres insertar:
                      </p>

                      <Input
                        name="instagram-url"
                        type="text"
                        placeholder="https://www.instagram.com/reel/ABC123/"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        options={{
                          skin: "base",
                          size: "md",
                          marginBottom: 0
                        }}
                      />

                      {error && <ErrorText>{error}</ErrorText>}

                      {isValid && (
                        <div style={{
                          marginTop: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#e8f5e8",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "#2d5a2d"
                        }}>
                          ✅ URL válida - Reel ID: {extractInstagramReelId(url)}
                        </div>
                      )}
                    </div>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      onClick={onClose}
                      style={{ marginRight: "12px" }}
                      options={{
                        type: "outline",
                        size: "md",
                        skin: "lightGray"
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={!isValid}
                      options={{
                        type: "filled",
                        size: "md",
                        skin: "purple"
                      }}
                    >
                      <Icon icon={linkIcon} size="16px" />
                      Insertar Reel
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </ModalDialog>
            </Modal>
          </ModalAnimation>

          <Animation name="fadeIn" className={`animation-${state}`}>
            <ModalBackdrop onClick={handleBackdropClick} />
          </Animation>
        </>
      )}
    </Transition>,
    modalDOM
  );
};

export default InsertInstagramReelsModal;
